import ObjectID from 'bson-objectid';
import { TFunction } from 'i18next';

import { PostFragment } from '@/features/messages/graphql/fragments/generated/Post';
import { CourseContentType, SenderTypeEnum } from '@/types/schema';

import { getKnowledgeAssetStaticCover } from '../corporate/components/LearningContent/Icon';
import { ChatbotMessageType, KnowledgeDocsTabs } from './types';

const suggestedCoachTopics = [
  { value: `A tactic that helped you close a high-stakes deal` },
  { value: `A small process tweak that saved you hours` },
  { value: `A habit that improved your productivity this month` },
  { value: `How you resolved a recurring bug more efficiently` },
  { value: `What you changed in your pitch that made the difference` },
  { value: `What worked well in your last client call or demo` },
];

export const getSuggestedCoachTopics = () => suggestedCoachTopics;

export const getDateFromStr = (dateStr: string): Date | null => {
  try {
    const date = new Date(dateStr);
    return date;
  } catch (e) {
    return null;
  }
};

const adaptReferences = (
  references: PostFragment['references'],
): ChatbotMessageType['references'] =>
  references
    ?.filter((reference: any) => reference && reference.referenceId)
    .map((reference) => {
      if (reference.referenceType === 'KNOWLEDGE_DOC') {
        return {
          id: reference.referenceRootId || '', // noteId
          rootId: reference.referenceId || '', // rootBlockId
          name: reference.title || 'Untitled',
          cover: { url: '' },
          type: reference.referenceType,
        };
      } else if (reference.referenceRootType === 'ARTICLE') {
        return {
          id: (reference.referenceMeta as CourseContentType)?.id || '',
          name: (reference.referenceMeta as CourseContentType)?.name || '',
          cover: (reference.referenceMeta as CourseContentType)?.cover || { url: '' },
          type: (reference.referenceMeta as CourseContentType)?.type || '',
        };
        // @ts-ignore
      } else if (reference.referenceRootType === 'KNOWLEDGE_ASSET') {
        const subtype = reference.referenceType;
        // @ts-ignore
        const image = getKnowledgeAssetStaticCover(subtype);
        return {
          id: reference.referenceId || '',
          name: reference.title || '',
          type: reference.referenceType || '',
          cover: { url: image },
        };
      } else if (reference.referenceRootType === 'COURSE') {
        return {
          id: reference.referenceId || '',
          rootId: reference.referenceRootId || '',
          parentId: reference.referenceParentId || '',
          type: reference.referenceType || '',
          cover: (reference.referenceMeta as CourseContentType)?.cover || { url: '' },
          name: reference.title || (reference.referenceMeta as CourseContentType)?.name || '',
        };
      } else {
        // integrations
        return {
          id: reference.referenceId || '',
          name: reference.title || '',
          cover: { url: reference.coverUrl || '' },
          type: reference.referenceType || '',
          url: reference.referenceUrl || '',
          isIntegration: true,
        };
      }
    });

export const convertMessageToChatbotMessage = (message: PostFragment): ChatbotMessageType => {
  return {
    id: message.id,
    senderType: message.senderType as SenderTypeEnum,
    textContent: message.message,
    doc: message.knowledgeDocument || undefined,
    updatedAt: getDateFromStr(message.updatedAt),
    references: adaptReferences(message.references),
  };
};

export const convertMessageToUserMessage = (text: string): ChatbotMessageType => {
  return {
    id: getNewObjectId(),
    senderType: SenderTypeEnum.User,
    textContent: text,
    updatedAt: null,
    references: [],
  };
};

export const getNewObjectId = () => {
  return new ObjectID().toHexString();
};

export const getKnowledgeDocsTabs = (t: TFunction) => [
  {
    label: t('Coach:NotesPanel.MyDocsTab'),
    value: KnowledgeDocsTabs.MyDocs,
  },
  {
    label: t('Coach:NotesPanel.PendingApprovalTab'),
    value: KnowledgeDocsTabs.PendingApproval,
  },
  {
    label: t('Coach:NotesPanel.ApprovedByMeTab'),
    value: KnowledgeDocsTabs.Approved,
  },
];

export const replaceMentionsWithSyntax = (html: string): string => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  const mentionDivs = doc.querySelectorAll('.ProseMirror__reactComponent');

  mentionDivs.forEach((mentionDiv) => {
    const mentionAnchor = mentionDiv.querySelector('a.mention');
    if (!mentionAnchor) return;

    const type = mentionAnchor.getAttribute('data-mention-type');
    const id = mentionAnchor.getAttribute('data-mention-id');
    const name = mentionAnchor.getAttribute('data-mention-name');

    if (type && id && name) {
      const mentionSyntax = `[${type}:ID:${id}:NAME:${name}]`;
      const textNode = document.createTextNode(mentionSyntax);

      const wrapperDiv = mentionDiv.closest('.ProseMirror__dom');
      if (wrapperDiv?.parentNode) {
        wrapperDiv.parentNode.replaceChild(textNode, wrapperDiv);
      }
    }
  });

  return doc.body.innerHTML;
};
